import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { ContactCell, ContactLines, bareInput, offerDirectoryAdd } from "@/components/ContactCell";

const GcContactList = ({ list, testid }) => (
  <ul className="space-y-1.5 text-xs" data-testid={testid}>
    {list.map((c) => (
      <li key={c.gc}>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{c.gc}</div>
        {c.name || c.email ? <ContactLines name={c.name} email={c.email} testid={`${testid}-${c.gc}`} /> : <span className="text-muted-foreground">—</span>}
      </li>))}
  </ul>
);

function GcContactEditor({ list, onSave, testid }) {
  const [draft, setDraft] = useState(list.map((c) => ({ ...c })));
  const set = (i, k) => (e) => setDraft((d) => d.map((c, j) => (j === i ? { ...c, [k]: e.target.value } : c)));
  const save = () => {
    const changed = draft.map((c) => ({ ...c, name: c.name.trim(), email: c.email.trim() }))
      .filter((c, i) => c.name !== (list[i].name || "") || c.email !== (list[i].email || ""));
    onSave(changed);
    changed.filter((c) => c.email).forEach((c) => offerDirectoryAdd({ email: c.email, name: c.name, companyName: c.gc, type: "gc" }));
  };
  return (
    <div className="space-y-2" data-testid={`${testid}-editor`} onKeyDown={(e) => e.key === "Enter" && save()}>
      {draft.map((c, i) => (
        <div key={c.gc} className="flex flex-col gap-0.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{c.gc}</div>
          <input autoFocus={i === 0} value={c.name} onChange={set(i, "name")} placeholder="Name" className={`${bareInput} w-full`} data-testid={`${testid}-name-${i}`} />
          <input value={c.email} onChange={set(i, "email")} placeholder="email@company.com" type="email" className={`${bareInput} w-full text-[11px]`} data-testid={`${testid}-email-${i}`} />
        </div>))}
      <Button size="sm" onClick={save} className="h-7 w-full bg-[#007BFF] text-xs text-white hover:bg-[#0069d9]" data-testid={`${testid}-save`}>Save</Button>
    </div>
  );
}

/** BOD GC contact — one contact per GC. One GC: the inline ContactCell. Several: first + "+N", hover lists all, click edits all. */
export function GcContactCell({ r, id, onSaveLegacy, onSaveGc }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const list = r.gc_contacts || [];
  const testid = `spec-gc-contact-${id}`;
  if (list.length <= 1) {
    const c = list[0];
    return <ContactCell name={c ? c.name : r.gc_contact_name} email={c ? c.email : r.gc_contact_email} testid={testid}
                        directory={{ companyName: c?.gc || "", type: "gc" }}
                        onSave={(d) => (c ? onSaveGc(r, [{ gc: c.gc, ...d }]) : onSaveLegacy(r, { gc_contact_name: d.name, gc_contact_email: d.email }))} />;
  }
  const shown = list.find((c) => c.email || c.name);
  const more = list.filter((c) => (c.email || c.name) && c !== shown).length;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setHover(false); }}>
      <HoverCard open={hover && !open} onOpenChange={setHover} openDelay={150}>
        <HoverCardTrigger asChild>
          <PopoverTrigger asChild>
            <div className="flex w-full cursor-text items-start gap-1.5 py-0.5 leading-tight" data-testid={testid}>
              {shown ? <ContactLines name={shown.name} email={shown.email} testid={testid} /> : <span className="py-0.5 text-muted-foreground">—</span>}
              {more > 0 && <span className="mt-0.5 shrink-0 rounded bg-muted px-1 text-[10px] font-medium text-muted-foreground" data-testid={`${testid}-more`}>+{more}</span>}
            </div>
          </PopoverTrigger>
        </HoverCardTrigger>
        <HoverCardContent align="start" className="w-64 p-2.5" data-testid={`${testid}-hover`}><GcContactList list={list} testid={`${testid}-list`} /></HoverCardContent>
      </HoverCard>
      <PopoverContent align="start" className="w-64 p-3" data-testid={`${testid}-pop`}>
        <GcContactEditor list={list} testid={testid} onSave={(changed) => { setOpen(false); if (changed.length) onSaveGc(r, changed); }} />
      </PopoverContent>
    </Popover>
  );
}
